import { useMemo } from 'react';
import { getTextMetrics } from '../../../utils/textMetrics';

interface TextMetricsBarProps {
    text: string;
    isMarkdown?: boolean;
    onCopy?: () => void;
}

/**
 * Compact horizontal metrics bar showing word count, sentence count,
 * reading grade (Flesch-Kincaid), and estimated reading time.
 */
const TextMetricsBar = ({ text, isMarkdown = false, onCopy }: TextMetricsBarProps) => {
    const metrics = useMemo(() => getTextMetrics(text, isMarkdown), [text, isMarkdown]);

    if (metrics.wordCount === 0) return null;

    return (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-2.5 py-1.5 mt-1 bg-gray-50 border border-gray-100 rounded-lg text-[10px] text-gray-400 font-medium">
            <span title="Word count" className="flex items-center gap-1">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                {metrics.wordCount} words
            </span>
            <span className="text-gray-200">·</span>
            <span title="Sentence count" className="flex items-center gap-1">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                {metrics.sentenceCount} sentences
            </span>
            <span className="text-gray-200">·</span>
            <span title="Flesch-Kincaid reading level" className={`flex items-center gap-1 ${metrics.gradeColor}`}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                {metrics.gradeLabel}
            </span>
            <span className="text-gray-200">·</span>
            <span title="Estimated reading time" className="flex items-center gap-1">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {metrics.readingTime}
            </span>
            {onCopy && (
                <>
                    <span className="text-gray-200">·</span>
                    <button
                        onClick={onCopy}
                        title="Copy review report to clipboard (original, final text, matter ref)"
                        aria-label="Copy review report"
                        className="flex items-center text-gray-300 hover:text-gray-500 transition-colors"
                    >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                    </button>
                </>
            )}
        </div>
    );
};

export default TextMetricsBar;
